import request from '../../../../util/APIUtils';
import { PORTAL_API_BASE_URL } from '../../../../constants';

/**
 * Fetches a single case plan with sections and widgets for the admin detail view.
 * @param {string} casePlanId - UUID of the case plan
 * @returns {Promise<{ plan: object, sections: object[] }>}
 */
export const fetchCasePlanDetail = (casePlanId, signal) =>
  request({
    url: `${PORTAL_API_BASE_URL}/api/admin/case-plan/${casePlanId}`,
    method: 'GET',
    signal,
  });

/**
 * Queues a case plan export for the selected sections.
 * @param {string} casePlanId - UUID of the case plan
 * @param {{ emails: string[], password: string, sectionIds: string[] }} payload
 */
export const exportCasePlan = (casePlanId, { emails, password, sectionIds }) =>
  request({
    url: `${PORTAL_API_BASE_URL}/api/admin/case-plan/${casePlanId}/export`,
    method: 'POST',
    body: JSON.stringify({
      emails,
      password,
      sectionIds,
    }),
  });

/**
 * Last-in-wins plan PATCH. Status-only body — do not send updatedAt.
 * @param {string} casePlanId - UUID of the case plan
 * @param {{ status: string }} payload
 */
export const patchCasePlan = (casePlanId, { status }) =>
  request({
    url: `${PORTAL_API_BASE_URL}/api/admin/case-plan/${casePlanId}`,
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });

/**
 * Last-in-wins widget PATCH. Description-only bodies skip config validation.
 * @param {string} casePlanId - UUID of the case plan
 * @param {string} widgetId - UUID of the widget
 * @param {{ config?: unknown, description?: string | null }} payload
 */
export const patchCasePlanWidget = (casePlanId, widgetId, { config, description }) =>
  request({
    url: `${PORTAL_API_BASE_URL}/api/admin/case-plan/${casePlanId}/widgets/${widgetId}`,
    method: 'PATCH',
    body: JSON.stringify({
      ...(config !== undefined ? { config } : {}),
      ...(description !== undefined ? { description } : {}),
    }),
  });

const buildEditorsUrl = (casePlanId) =>
  `${PORTAL_API_BASE_URL}/api/admin/case-plan/${casePlanId}/editors`;

/**
 * Lists active (`is_active = true`) editors for a plan.
 * @param {string} casePlanId - UUID of the case plan
 * @param {AbortSignal} [signal]
 * @returns {Promise<{ editors: object[] }>}
 */
export const fetchCasePlanEditors = (casePlanId, signal) =>
  request({
    url: buildEditorsUrl(casePlanId),
    method: 'GET',
    signal,
  });

/**
 * Replaces the active editor list. Body is the chips still on the modal after local add/remove.
 * @param {string} casePlanId - UUID of the case plan
 * @param {{ editorIds: number[] }} payload
 * @returns {Promise<{ editors: object[] }>}
 */
export const patchCasePlanEditors = (casePlanId, { editorIds }) =>
  request({
    url: buildEditorsUrl(casePlanId),
    method: 'PATCH',
    body: JSON.stringify({ editorIds }),
  });

const buildSectionsUrl = (casePlanId) =>
  `${PORTAL_API_BASE_URL}/api/admin/case-plan/${casePlanId}/sections`;

const buildSectionUrl = (casePlanId, sectionId) =>
  `${buildSectionsUrl(casePlanId)}/${sectionId}`;

/**
 * Creates the placeholder section the Add Section control opens in edit mode.
 * `insertBeforeSectionId` is a required key: a section id inserts above that section,
 * `null` appends after the last active section.
 *
 * @param {string} casePlanId - UUID of the case plan
 * @param {{ insertBeforeSectionId: string | null }} payload
 * @returns {Promise<object>} Created section (201), including its empty `widgets` array
 */
export const createCasePlanSection = (casePlanId, { insertBeforeSectionId }) =>
  request({
    url: buildSectionsUrl(casePlanId),
    method: 'POST',
    body: JSON.stringify({ insertBeforeSectionId: insertBeforeSectionId ?? null }),
    suppressErrorToast: true,
  });

/**
 * Saves section fields (last write wins). Send only the fields being changed;
 * the API rejects an empty body.
 *
 * Due to a config issue we had to go this route again: POST instead of PATCH.
 *
 * @param {string} casePlanId - UUID of the case plan
 * @param {string} sectionId - UUID of the section
 * @param {{ name?: string, description?: string | null, visibleToLearner?: boolean }} fields
 * @returns {Promise<object>} Updated section, including its `widgets`
 */
export const updateCasePlanSection = (casePlanId, sectionId, fields) =>
  request({
    url: buildSectionUrl(casePlanId, sectionId),
    method: 'POST',
    body: JSON.stringify(fields),
    suppressErrorToast: true,
  });

/**
 * Reorders a section among its active siblings by placing it at the target slot.
 * `sortOrder` is the final index among active sections (`0..n-1`); matching the
 * current index is a server-side no-op.
 *
 * @param {string} casePlanId - UUID of the case plan
 * @param {string} sectionId - UUID of the section
 * @param {number} sortOrder - Target slot among active sections
 * @returns {Promise<object>} Moved section, including its `widgets`
 */
export const moveCasePlanSection = (casePlanId, sectionId, sortOrder) =>
  request({
    url: `${buildSectionUrl(casePlanId, sectionId)}/move`,
    method: 'POST',
    body: JSON.stringify({ sortOrder }),
    suppressErrorToast: true,
  });

/**
 * Soft-deletes a section. Draft plans only; the API answers 409 when another
 * active section still depends on it.
 *
 * @param {string} casePlanId - UUID of the case plan
 * @param {string} sectionId - UUID of the section
 * @returns {Promise<object>} Archived section, including its `widgets`
 */
export const deleteCasePlanSection = (casePlanId, sectionId) =>
  request({
    url: buildSectionUrl(casePlanId, sectionId),
    method: 'DELETE',
    suppressErrorToast: true,
  });

/**
 * Archives a section. Active and Released plans only.
 *
 * @param {string} casePlanId - UUID of the case plan
 * @param {string} sectionId - UUID of the section
 * @returns {Promise<object>} Archived section, including its `widgets`
 */
export const archiveCasePlanSection = (casePlanId, sectionId) =>
  request({
    url: `${buildSectionUrl(casePlanId, sectionId)}/archive`,
    method: 'POST',
    suppressErrorToast: true,
  });
